import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Container,
    Drawer,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { Checkbox } from '@/components/Checkbox/Checkbox';
import { CouponSearch } from '@/pageContents/couponsContent/CouponSearch';
import { debounce } from 'lodash';
import { useGetCouponsQuery } from '@/api/v2/coupon';
import { CouponListItem, CreateCoupon, createNewCoupon } from '@/models/coupon';
import { CreateCouponDrawerContent } from '@/pageContents/couponsContent/CouponDrawerContent';
import { CouponEditDrawerContent } from '@/pageContents/couponsContent/CouponEditDrawerContent';
import { StarjackTable } from '@/components/Table/v2/Table';
import { getMoneyAsString } from '@/utils';
import { formatStarjackDate } from '@/utils/time';

export const CouponPageContent: FunctionComponent = () => {
    const theme = useTheme();

    // The server pages, filters and sorts (see CouponListQuery in the order
    // service) — this component only holds the request state.
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [searchTerm, setSearchTerm] = useState('');
    // Hidden by default and shown on demand — same pattern as lift operators and
    // webshops, agreed with Sarah instead of splitting the list across two tabs.
    const [showInactive, setShowInactive] = useState(false);
    const [sort, setSort] = useState('CREATED_AT');
    const [direction, setDirection] = useState<'asc' | 'desc'>('desc');

    const {
        data: couponPage,
        isFetching,
        isError,
    } = useGetCouponsQuery({
        page,
        size: rowsPerPage,
        query: searchTerm || undefined,
        ...(showInactive ? {} : { active: true, expired: false }),
        sort,
        direction,
    });

    // A mutation can empty the current page (deactivating the last coupon of the
    // last page while inactive ones are hidden). Clamp back to the last valid page.
    useEffect(() => {
        if (!couponPage) return;
        const lastPage = Math.max(
            0,
            Math.ceil(couponPage.total / rowsPerPage) - 1
        );
        if (page > lastPage) {
            setPage(lastPage);
        }
    }, [couponPage, page, rowsPerPage]);

    const [drawerConfig, setDrawerConfig] = useState<{
        isOpen: boolean;
        coupon: CouponListItem | CreateCoupon | null;
    }>({ isOpen: false, coupon: null });

    const onSearchChange = useCallback(
        debounce((value: string) => {
            setSearchTerm(value);
            setPage(0);
        }, 250),
        []
    );

    const onCloseDrawer = () => {
        setDrawerConfig((prevState) => ({
            ...prevState,
            isOpen: false,
        }));
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const getStatusChip = (coupon: CouponListItem) => {
        if (!coupon.active) {
            return <Chip label='Deaktiviert' color='error' size='small' />;
        }
        if (new Date(coupon.validUntil) < today) {
            return <Chip label='Abgelaufen' size='small' />;
        }
        return <Chip label='Aktiv' color='success' size='small' />;
    };

    const isEdit = (
        coupon: CouponListItem | CreateCoupon
    ): coupon is CouponListItem => 'id' in coupon && !!coupon.id;

    return (
        <Container
            sx={{
                mt: 3,
                ml: 'unset',
            }}
            maxWidth={false}
        >
            <Typography
                sx={{
                    mb: 8,
                }}
                variant='h4'
            >
                Coupons
            </Typography>
            <Stack spacing={theme.page.verticalSpacing}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <CouponSearch onChange={onSearchChange} />
                    <Button
                        variant='contained'
                        onClick={() => {
                            setDrawerConfig({
                                isOpen: true,
                                coupon: createNewCoupon(),
                            });
                        }}
                    >
                        + Neuen Coupon anlegen
                    </Button>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                        label='Abgelaufene und deaktivierte einblenden'
                        onChange={(_event, checked) => {
                            setShowInactive(checked);
                            setPage(0);
                        }}
                    />
                </Box>

                <StarjackTable<CouponListItem>
                    itemId='id'
                    count={couponPage?.total ?? 0}
                    items={couponPage?.coupons ?? []}
                    currentPage={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={setPage}
                    onRowsPerPageChange={(rows) => {
                        // Landing beyond the last page shows an empty slice.
                        setRowsPerPage(rows);
                        setPage(0);
                    }}
                    sortColumn={sort}
                    sortDirection={direction}
                    onSortChange={(nextSort, nextDirection) => {
                        setSort(nextSort);
                        setDirection(nextDirection);
                        setPage(0);
                    }}
                    loading={isFetching}
                    onRowClick={(coupon) => {
                        setDrawerConfig({
                            isOpen: true,
                            coupon,
                        });
                    }}
                    noDataLabel={
                        isError
                            ? 'Coupons konnten nicht geladen werden.'
                            : showInactive
                              ? 'Keine Coupons vorhanden.'
                              : 'Keine aktiven Coupons vorhanden.'
                    }
                    columns={[
                        {
                            label: 'Name',
                            sortKey: 'NAME',
                            itemTransform: (coupon) => (
                                <Typography
                                    variant='body2'
                                    sx={{
                                        maxWidth: 250,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {coupon.name}
                                </Typography>
                            ),
                        },
                        {
                            label: 'Betrag',
                            sortKey: 'AMOUNT',
                            itemTransform: (coupon) =>
                                getMoneyAsString(coupon.amount),
                        },
                        {
                            label: 'Gültig ab',
                            sortKey: 'VALID_FROM',
                            itemTransform: (coupon) =>
                                coupon.validFrom
                                    ? formatStarjackDate(
                                          new Date(coupon.validFrom)
                                      )
                                    : '-',
                        },
                        {
                            label: 'Gültig bis',
                            sortKey: 'VALID_UNTIL',
                            itemTransform: (coupon) =>
                                formatStarjackDate(new Date(coupon.validUntil)),
                        },
                        {
                            label: 'Codes',
                            sortKey: 'CODES',
                            itemTransform: (coupon) => coupon.codeCount,
                        },
                        {
                            label: 'Verbraucht',
                            sortKey: 'USED',
                            itemTransform: (coupon) => coupon.usedCount,
                        },
                        {
                            label: 'Bergbahn',
                            itemTransform: (coupon) => (
                                <Typography
                                    variant='body2'
                                    sx={{
                                        color: coupon.liftOperatorName
                                            ? 'inherit'
                                            : 'text.secondary',
                                    }}
                                >
                                    {coupon.liftOperatorName ||
                                        (coupon.liftOperatorId
                                            ? 'Unbekannte Bergbahn'
                                            : 'Global')}
                                </Typography>
                            ),
                        },
                        {
                            label: 'Status',
                            sortKey: 'ACTIVE',
                            itemTransform: (coupon) => getStatusChip(coupon),
                        },
                    ]}
                />

                <Drawer
                    anchor='right'
                    open={drawerConfig.isOpen}
                    onClose={onCloseDrawer}
                >
                    {drawerConfig.coupon &&
                        (isEdit(drawerConfig.coupon) ? (
                            <CouponEditDrawerContent
                                coupon={drawerConfig.coupon}
                                onClose={onCloseDrawer}
                            />
                        ) : (
                            <CreateCouponDrawerContent
                                coupon={drawerConfig.coupon}
                                onClose={onCloseDrawer}
                            />
                        ))}
                </Drawer>
            </Stack>
        </Container>
    );
};
